import React, { useState, useEffect } from 'react';
import { sendRequest } from "./vmUtils";
import { lambdaURL } from "../config";

export const VMProfileFilters = ({ assemblyId, onFiltersChange }) => {
  const [sections, setSections] = useState([]);
  const [questions, setQuestions] = useState({});
  const [filters, setFilters] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchProfileData = async () => {
      if (!assemblyId) return;
      
      setIsLoading(true);
      try {
        let jsonData = {
          command: "getProfileSections",
          assemblyId: assemblyId
        };
        const sectionsResponse = await sendRequest(lambdaURL, jsonData);
        
        if (sectionsResponse.body) {
          const parsedSections = JSON.parse(sectionsResponse.body);
          setSections(parsedSections.sort((a, b) => a.order - b.order));
          
          const questionMap = {};
          for (const section of parsedSections) {
            const sectionId = section.SK.replace('METADATA#', '');
            const questionsResponse = await sendRequest(lambdaURL, {
              command: "getProfileQuestions",
              sectionId: sectionId
            });
            
            if (questionsResponse.body) {
              const parsedQuestions = JSON.parse(questionsResponse.body);
              questionMap[section.SK.replace('METADATA#', '')] = 
                parsedQuestions.sort((a, b) => a.order - b.order);
            }
          }
          setQuestions(questionMap);
        }
        
        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching profile data:", error);
        setIsLoading(false);
      }
    };

    fetchProfileData();
  }, [assemblyId]);

  const FilterRow = ({ filter, index }) => {
    const currentSection = sections.find(s => s.SK.replace('METADATA#', '') === filter.sectionId);
    const currentQuestions = questions[filter.sectionId] || [];
    const currentQuestion = currentQuestions.find(q => q.SK.replace('PROFILE_QUESTION#', '') === filter.questionId);
    const options = currentQuestion?.options || [];

    return (
      <div className="flex items-center gap-4 mb-4 p-2 bg-gray-50 rounded ml-8">
        <select
          className="vm-filter-dropdown vm-form-input"
          value={filter.sectionId}
          onChange={(e) => handleFilterChange(index, 'sectionId', e.target.value)}
        >
          <option value="">Select Section</option>
          {sections.map(section => (
            <option key={section.SK} value={section.SK.replace('METADATA#', '')}>
              {section.title}
            </option>
          ))}
        </select>

        <select
          className="vm-filter-dropdown vm-form-input"
          value={filter.questionId}
          onChange={(e) => handleFilterChange(index, 'questionId', e.target.value)}
        >
          <option value="">Select Question</option>
          {currentQuestions.map(question => (
            <option 
              key={question.SK} 
              value={question.SK.replace('PROFILE_QUESTION#', '')}
            >
              {question.questionText}
            </option>
          ))}
        </select>

        <div className="flex flex-wrap gap-2">
          {options.map(option => (
            <label key={option} className="flex items-center gap-1">
              <input
                type="checkbox"
                checked={filter.selectedAnswers.includes(option)}
                onChange={(e) => {
                  const newAnswers = e.target.checked
                    ? [...filter.selectedAnswers, option]
                    : filter.selectedAnswers.filter(a => a !== option);
                  handleFilterChange(index, 'selectedAnswers', newAnswers);
                }}
              />
              <span className="text-sm">{option}</span>
            </label>
          ))}
        </div>

        <button
          onClick={() => removeFilter(index)}
          className="vm-form-button"
        >
          Remove
        </button>
      </div>
    );
  };

  const addFilter = () => {
    const newFilter = {
      sectionId: '',
      questionId: '',
      selectedAnswers: []
    };
    setFilters([...filters, newFilter]);
  };

  const removeFilter = (index) => {
    const newFilters = filters.filter((_, i) => i !== index);
    setFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const handleFilterChange = (index, field, value) => {
    const newFilters = filters.map((filter, i) => {
      if (i === index) {
        if (field === 'sectionId' || field === 'questionId') {
          return {
            ...filter,
            [field]: value,
            selectedAnswers: []
          };
        }
        return {
          ...filter,
          [field]: value
        };
      }
      return filter;
    });
    setFilters(newFilters);
    onFiltersChange(newFilters);
  };

  if (isLoading) {
    return <div>Loading filters...</div>;
  }

  return (
    <div className="mb-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold ml-8">Profile Filters</h3>
        <button
          onClick={addFilter}
          className="vm-form-button mr-8"
        >
          Add Filter
        </button>
      </div>

      <div className="space-y-2">
        {filters.map((filter, index) => (
          <FilterRow 
            key={index} 
            filter={filter} 
            index={index} 
          />
        ))}
      </div>
    </div>
  );
};
